<script setup lang="ts">
import { type FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import type { MaskOptions, MaskType } from 'maska';
import InputText, { type InputTextProps } from 'primevue/inputtext';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { ref } from 'vue';

export interface VInputMaskProps {
  disabled?: boolean;
  fullWidth?: boolean;
  hasError?: boolean;
  icon?: FontAwesomeIconProps['icon'];
  iconClickable?: boolean;
  invalid?: InputTextProps['invalid'];
  /**
   * Zie https://beholdr.github.io/maska/v3/#/tokens voor syntax.
   */
  mask: MaskOptions | MaskType;
  placeholder?: string;
  pt?: InputTextProps['pt'];
  readonly?: boolean;
  unmask?: boolean;
  id?: string;
}

const props = defineProps<VInputMaskProps>();

const emit = defineEmits<{ focus: []; blur: []; iconClicked: [] }>();
defineOptions({ inheritAttrs: false });

const unmaskedValue = ref('');
const model = defineModel<InputTextProps['modelValue']>({
  set: (maskedValue) => (props.unmask ? unmaskedValue.value : maskedValue),
});

defineExpose({ unmaskedValue });
</script>

<template>
  <div
    v-if="!readonly"
    :class="{ 'w-full': props.fullWidth }"
    class="relative inline-flex max-w-full flex-row items-center"
  >
    <!-- @vue-expect-error   @focus en @blur worden niet goed getypt-->
    <InputText
      :id="props.id"
      v-bind="{ ...$attrs, ...props }"
      v-maska:unmaskedValue.unmasked="props.mask"
      v-model="model"
      class="styled-input"
      :class="[
        'filled',
        { '!pr-[2.875rem]': icon && !readonly, '!border-red-500': props.hasError },
      ]"
      :pt="props.pt"
      @focus="emit('focus')"
      @blur="emit('blur')"
    />
    <div
      class="absolute right-0 pr-4 text-grey-700"
      :class="{ 'cursor-pointer': props.iconClickable }"
      v-if="icon && !disabled && !readonly"
      @click="props.iconClickable && emit('iconClicked')"
    >
      <FontAwesomeIcon :icon="icon" />
    </div>
  </div>
  <div v-else class="filled group-[]/float-label:h-11 group-[]/float-label:content-center">
    {{ model ?? '-' }}
  </div>
</template>
